import React, { useState, useRef } from 'react';

const MALImporter = ({ onImportComplete }) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setResults(null);
    }
  };

  const parseMALXML = async (xmlContent) => {
    return new Promise((resolve, reject) => {
      try {
        // Create a DOM parser to parse the XML content
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlContent, "text/xml");
        
        // Check for parsing errors
        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
          return reject(new Error("XML parsing error"));
        }

        // Get all anime entries
        const animeEntries = xmlDoc.querySelectorAll('anime');
        
        if (!animeEntries || animeEntries.length === 0) {
          return reject(new Error("No anime entries found in the XML file"));
        }
        
        const animeList = [];
        
        // Process each anime entry
        animeEntries.forEach((animeNode) => {
          // Helper function to safely extract text content
          const getTextContent = (node, selector) => {
            const element = node.querySelector(selector);
            return element ? element.textContent : null;
          };
          
          // Extract anime data
const anime = {
  mal_id: parseInt(getTextContent(animeNode, 'series_animedb_id'), 10) || null,
  title: getTextContent(animeNode, 'series_title'),
  status: getTextContent(animeNode, 'my_status'), // This will be "Completed", "Watching", etc.
  score: parseInt(getTextContent(animeNode, 'my_score'), 10) || 0,
  watched_episodes: parseInt(getTextContent(animeNode, 'my_watched_episodes'), 10) || 0,
  total_episodes: parseInt(getTextContent(animeNode, 'series_episodes'), 10) || 0,
  start_date: getTextContent(animeNode, 'my_start_date'),
  end_date: getTextContent(animeNode, 'my_finish_date'),
  type: getTextContent(animeNode, 'series_type'),
  tags: getTextContent(animeNode, 'my_tags'),
  notes: getTextContent(animeNode, 'my_comments'),
  rewatch_count: parseInt(getTextContent(animeNode, 'my_times_watched'), 10) || 0
};

// For debugging
console.log(`Parsed anime: ${anime.title} - Status: "${anime.status}"`);
          
          // Process status - MAL uses numeric codes
          switch (anime.status_numeric) {
            case 1:
              anime.status_text = "Currently Watching";
              break;
            case 2:
              anime.status_text = "Completed";
              break;
            case 3:
              anime.status_text = "On Hold";
              break;
            case 4:
              anime.status_text = "Dropped";
              break;
            case 6:
              anime.status_text = "Plan to Watch";
              break;
            default:
              anime.status_text = "Unknown";
          }
          
          animeList.push(anime);
        });
        
        resolve(animeList);
      } catch (err) {
        reject(err);
      }
    });
  };

  const importMALData = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }
    
    setImporting(true);
    setProgress(0);
    setError(null);
    
    try {
      // Read the file contents
      const fileContent = await readFileAsText(file);
      
      // Parse the XML
      const animeList = await parseMALXML(fileContent);
      setProgress(25);
      
      // For larger lists, use the bulk import endpoint
      if (animeList.length > 10) {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Authentication required');
        
        setProgress(40);
        
        // Send all entries to bulk import endpoint
        const response = await fetch(`${API_BASE_URL}/api/import/mal/bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ entries: animeList })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Bulk import failed');
        }
        
        const bulkResults = await response.json();
        setProgress(90);
        setResults(bulkResults.results);
        
        // Notify parent component
        if (onImportComplete) {
          onImportComplete(bulkResults.results);
        }
      } else {
        // For smaller lists, process entries individually
        const importResults = {
          total: animeList.length,
          processed: 0,
          succeeded: 0,
          failed: 0,
          skipped: 0,
          details: []
        };
        
        // Process each anime entry
        for (let i = 0; i < animeList.length; i++) {
          const anime = animeList[i];
          setProgress(25 + Math.floor((i / animeList.length) * 60));
          
          try {
            // Check if the anime exists in your database
            const animeExists = await checkAnimeExists(anime.mal_id);
            
            if (!animeExists) {
              // Skip if not in the database
              importResults.skipped++;
              importResults.details.push({
                title: anime.title,
                status: 'skipped',
                reason: 'Anime not found in database'
              });
              continue;
            }
            
            // Add to user's list
            await addToUserList(anime);
            
            importResults.succeeded++;
            importResults.details.push({
              title: anime.title,
              status: 'success'
            });
          } catch (err) {
            importResults.failed++;
            importResults.details.push({
              title: anime.title,
              status: 'failed',
              reason: err.message
            });
          }
          
          importResults.processed++;
        }
        
        setProgress(90);
        setResults(importResults);
        
        // Notify parent component
        if (onImportComplete) {
          onImportComplete(importResults);
        }
      }
    } catch (err) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
      setProgress(100);
    }
  };
  
  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };
  
  const checkAnimeExists = async (malId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/animes/mal/${malId}`);
      return response.ok;
    } catch (err) {
      console.error(`Error checking if anime exists: ${err.message}`);
      return false;
    }
  };
  
  const addToUserList = async (anime) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication required');
      
      const response = await fetch(`${API_BASE_URL}/api/import/mal/anime`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mal_id: anime.mal_id,
          status: anime.status_text,
          score: anime.score,
          watched_episodes: anime.watched_episodes,
          notes: anime.notes
        })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add anime to list');
      }
      
      return true;
    } catch (err) {
      console.error(`Error adding anime to list: ${err.message}`);
      throw err;
    }
  };

  return (
    <div className="mal-importer">
      <div className="mb-4">
        <p className="import-description" style={{ marginBottom: '15px', color: 'rgba(255, 255, 255, 0.8)' }}>
          Export your anime list from MyAnimeList and upload the XML file here.
          We'll import your anime list with your scores, progress, and status.
        </p>
        
        <div className="import-file-select">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".xml"
            className="hidden"
            style={{ display: 'none' }}
            disabled={importing}
          />
          <button
            onClick={() => fileInputRef.current.click()}
            className="edit-profile-btn"
            disabled={importing}
            style={{ marginRight: '10px' }}
          >
            Select File
          </button>
          <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
            {file ? file.name : 'No file selected'}
          </span>
        </div>
      </div>
      
      {error && (
        <div style={{ 
          marginBottom: '15px', 
          padding: '10px', 
          background: 'rgba(229, 62, 62, 0.2)', 
          color: '#FFA5A5', 
          borderRadius: '8px', 
          border: '1px solid rgba(229, 62, 62, 0.3)' 
        }}>
          {error}
        </div>
      )}
      
      <button
        onClick={importMALData}
        disabled={!file || importing}
        className="edit-profile-btn"
        style={{ 
          width: '100%', 
          marginBottom: '15px',
          opacity: !file || importing ? 0.7 : 1
        }}
      >
        {importing ? 'Importing...' : 'Import List'}
      </button>
      
      {importing && (
        <div style={{ marginBottom: '15px' }}>
          <div style={{ 
            width: '100%', 
            height: '6px', 
            background: 'rgba(255, 255, 255, 0.1)', 
            borderRadius: '3px', 
            overflow: 'hidden'
          }}>
            <div 
              style={{ 
                height: '100%', 
                width: `${progress}%`, 
                background: 'var(--accent)', 
                borderRadius: '3px',
                transition: 'width 0.3s ease'
              }}
            ></div>
          </div>
          <p style={{ 
            textAlign: 'center', 
            fontSize: '0.9rem', 
            color: 'rgba(255, 255, 255, 0.6)', 
            marginTop: '5px' 
          }}>
            Processing... {progress}%
          </p>
        </div>
      )}
      
      {results && (
        <div className="results-summary">
          <h3 style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '10px' }}>Import Results</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(4, 1fr)', 
            gap: '8px', 
            textAlign: 'center',
            marginBottom: '15px'
          }}>
            <div style={{ padding: '10px', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
              <p style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{results.total}</p>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>Total</p>
            </div>
            <div style={{ padding: '10px', background: 'rgba(72, 187, 120, 0.1)', borderRadius: '8px' }}>
              <p style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#68D391' }}>{results.succeeded}</p>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>Imported</p>
            </div>
            <div style={{ padding: '10px', background: 'rgba(236, 201, 75, 0.1)', borderRadius: '8px' }}>
              <p style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#F6E05E' }}>{results.skipped}</p>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>Skipped</p>
            </div>
            <div style={{ padding: '10px', background: 'rgba(229, 62, 62, 0.1)', borderRadius: '8px' }}>
              <p style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#FC8181' }}>{results.failed}</p>
              <p style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>Failed</p>
            </div>
          </div>
          
          {results.failed > 0 && (
            <div style={{ marginTop: '15px' }}>
              <h4 style={{ fontWeight: 'bold', marginBottom: '8px' }}>Issues</h4>
              <div style={{ 
                maxHeight: '150px', 
                overflowY: 'auto', 
                padding: '5px',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px'
              }}>
                <ul style={{ fontSize: '0.9rem' }}>
                  {results.details
                    .filter(item => item.status === 'failed')
                    .map((item, index) => (
                      <li key={index} style={{ marginBottom: '5px', paddingBottom: '5px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        <span style={{ color: '#FC8181' }}>•</span> {item.title}: {item.reason}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
      
      <div style={{ 
        marginTop: '20px', 
        fontSize: '0.8rem', 
        color: 'rgba(255, 255, 255, 0.5)',
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '10px',
        borderRadius: '8px'
      }}>
        <p>Having trouble? Make sure you:</p>
        <ul style={{ listStyleType: 'disc', marginLeft: '20px', marginTop: '5px' }}>
          <li>Export from MAL using "Export as XML" option</li>
          <li>Select "All Anime" when exporting</li>
          <li>Don't modify the XML file after downloading</li>
        </ul>
      </div>
    </div>
  );
};

export default MALImporter;